import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { DateOverlay } from "@/components/date-overlay/overlay";
import { Layout } from "@/components/layout";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import { TimezoneProvider } from "@/contexts/timezone-context";
import { AllMachinesPage } from "@/pages/all-machines";
import { CiJobDetailPage } from "@/pages/ci-job-detail";
import { CiJobsPage } from "@/pages/ci-jobs";
import { CiMatrixPage } from "@/pages/ci-matrix";
import { CiOverviewPage } from "@/pages/ci-overview";
import { CiOverviewDetailPage } from "@/pages/ci-overview-detail";
import { CiRunDetailPage } from "@/pages/ci-run-detail";
import { CiRunsPage } from "@/pages/ci-runs";
import { CiTestDetailPage } from "@/pages/ci-test-detail";
import { CiTofaPage } from "@/pages/ci-tofa";
import { CiTrendsPage } from "@/pages/ci-trends";
import { CiTriggerPage } from "@/pages/ci-trigger";
import { K8sClustersPage } from "@/pages/k8s-clusters";
import { LoginPage } from "@/pages/login";
import { MachineDetailPage } from "@/pages/machine-detail";
import { MyMachinesPage } from "@/pages/my-machines";
import { NotFoundPage } from "@/pages/not-found";
import { ProductionMachinesPage } from "@/pages/production-machines";
import { ReservedCapacityPage } from "@/pages/reserved-capacity";
import { SettingsPage } from "@/pages/settings";
import { SpendPage } from "@/pages/spend";
import { SpotPoolPage } from "@/pages/spot-pool";
import { SpotStatisticsPage } from "@/pages/spot-statistics";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

function RequireAuth() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <TimezoneProvider>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route element={<RequireAuth />}>
                  <Route element={<Layout />}>
                    <Route path="/" element={<AllMachinesPage />} />
                    <Route path="/machines/:id" element={<MachineDetailPage />} />
                    <Route path="/my-machines" element={<MyMachinesPage />} />
                    <Route path="/pools" element={<SpotPoolPage />} />
                    <Route path="/spot-statistics" element={<SpotStatisticsPage />} />
                    <Route path="/preemptions" element={<SpotStatisticsPage />} />
                    <Route path="/spot-pool" element={<Navigate to="/pools" replace />} />
                    <Route path="/reserved-capacity" element={<ReservedCapacityPage />} />
                    <Route path="/k8s-clusters" element={<K8sClustersPage />} />
                    <Route path="/production-machines" element={<ProductionMachinesPage />} />
                    <Route path="/ci" element={<CiRunsPage />} />
                    <Route path="/ci/jobs" element={<CiJobsPage />} />
                    <Route path="/ci/jobs/:jobId" element={<CiJobDetailPage />} />
                    <Route path="/ci/overview" element={<CiOverviewPage />} />
                    <Route path="/ci/overview/g" element={<CiOverviewDetailPage />} />
                    <Route path="/ci/matrix" element={<CiMatrixPage />} />
                    <Route path="/ci/matrix/tofa" element={<CiTofaPage />} />
                    <Route path="/ci/trends" element={<CiTrendsPage />} />
                    <Route path="/ci/trigger" element={<CiTriggerPage />} />
                    <Route path="/ci/:runId" element={<CiRunDetailPage />} />
                    <Route path="/ci/:runId/tests/:testId" element={<CiTestDetailPage />} />
                    <Route path="/spend" element={<SpendPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    {/* Catch-all 404. Sits inside RequireAuth+Layout on purpose:
                        the pathless parents put this splat at root path-space, so
                        it catches *every* unmatched URL (a top-level "*" would be
                        dead code — the nested splat always out-ranks it). Signed-in
                        users get the in-shell 404; signed-out users hit RequireAuth
                        and are redirected to /login rather than shown which routes
                        exist. */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </TimezoneProvider>
      </TooltipProvider>
      <Toaster richColors position="bottom-right" closeButton />
      <DateOverlay />
    </QueryClientProvider>
  );
}
